import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import type { Question } from "@services/evaluationService";
import L1EvaluationCard from "./L1EvaluationCard";
import SMEEvaluationForm, { type SMEFormData } from "./SMEEvaluationForm";
import L1SMEEvaluationForm from "./L1SMEEvaluationForm";
import StudentChallengeCard, {
  type RevisedEvaluationData,
} from "./StudentChallengeCard";
import styles from "./EvaluationCard.module.css";
import { splitIntoSentences } from "@utils/textUtils";
import type { AttemptStatus } from "@constants/evaluationConstants";

type EvaluationVariant = "L1Evaluated" | "SMEEvaluation" | "StudentChallenged" | "L1SMEEvaluation";

interface EvaluationContainerProps {
  question: Question;
  onApprovalValidityChange: (isValid: boolean) => void;
  onEvaluationChange?: (
    data: {
      awarded_marks: number;
      feedbacks: string[];
      attempt_status: AttemptStatus;
    } | null
  ) => void;
}

function determineVariant(question: Question): EvaluationVariant {
  // StudentChallenged: student_comment is available
  if (question.student_comment && question.student_comment.length > 0) {
    return "StudentChallenged";
  }

  // If current_evaluation exists, show SMEEvaluation form (pre-filled, read-only)
  if (question.previous_evaluation_result && question.current_evaluation) {
    return "SMEEvaluation";
  }

  // L1Evaluated: previous_evaluation_result is available and no student_comment
  if (question.previous_evaluation_result) {
    return "L1Evaluated";
  }

  // Default to L1Evaluated (this shouldn't happen based on requirements)
  return "L1SMEEvaluation";
}

export default function EvaluationContainer({
  question,
  onApprovalValidityChange,
  onEvaluationChange,
}: EvaluationContainerProps) {
  const initialVariant = determineVariant(question);

  const [currentVariant, setCurrentVariant] =
    useState<EvaluationVariant>(initialVariant);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isOverrideMode, setIsOverrideMode] = useState(false);
  const [formData, setFormData] = useState<SMEFormData | RevisedEvaluationData>(
    {
      marks: "",
      remarks: "",
      attemptStatus: undefined,
    }
  );

  // Update variant when question changes
  useEffect(() => {
    const newVariant = determineVariant(question);
    setCurrentVariant(newVariant);

    // If current_evaluation exists, this question is already evaluated
    if (question.current_evaluation) {
      setIsAccepted(true);
      setIsOverrideMode(false);
      setFormData({
        marks: String(question.current_evaluation.awarded_marks),
        remarks: question.current_evaluation.feedbacks.join(" ") || "",
      });
      onApprovalValidityChange(true);
      // No new data to save - emit null (user is just viewing already saved evaluation)
      if (onEvaluationChange) {
        onEvaluationChange(null);
      }
    } else {
      setIsAccepted(false);
      setIsOverrideMode(false);
      setFormData({ marks: "", remarks: "" });

      // Update approval validity based on new variant (initially false if no current_evaluation)
      if (newVariant === "StudentChallenged") {
        onApprovalValidityChange(false);
      } else if (newVariant === "L1Evaluated") {
        // L1Evaluated now requires explicit acceptance, so initially false
        onApprovalValidityChange(false);
      }
      else if(newVariant === "L1SMEEvaluation")
      {
        onApprovalValidityChange(false);
      }
    }
  }, [question, onApprovalValidityChange, onEvaluationChange]);

  const handleAccept = useCallback(() => {
    setIsAccepted(true);
    // When accepted, approval is valid (user can proceed)
    onApprovalValidityChange(true);
    if (onEvaluationChange && question.previous_evaluation_result) {
      onEvaluationChange({
        awarded_marks: question.previous_evaluation_result.awarded_marks,
        feedbacks: question.previous_evaluation_result.feedbacks,
        attempt_status: question.previous_evaluation_result.attempt_status,
      });
    }
  }, [onApprovalValidityChange, onEvaluationChange, question]);

  const handleOverride = useCallback(() => {
    setCurrentVariant("SMEEvaluation");
    setIsOverrideMode(true);
    // When entering override mode, approval is disabled until form is valid
    onApprovalValidityChange(false);
  }, [onApprovalValidityChange]);

  const handleCancel = useCallback(() => {
    // If we have current_evaluation, go back to read-only SME form
    if (question.current_evaluation) {
      setIsOverrideMode(false);
      setFormData({
        marks: String(question.current_evaluation.awarded_marks),
        remarks: question.current_evaluation.feedbacks.join(" ") || "",
      });
      onApprovalValidityChange(true);
      // No new data to save - emit null (user is just viewing already saved evaluation)
      if (onEvaluationChange) {
        onEvaluationChange(null);
      }
    } else {
      // Otherwise, go back to L1Evaluated
      setFormData({ marks: "", remarks: "" });
      setCurrentVariant("L1Evaluated");
      setIsOverrideMode(false);
      // Restore approval validity based on accepted state
      onApprovalValidityChange(isAccepted);
    }
  }, [
    question.current_evaluation,
    isAccepted,
    onApprovalValidityChange,
    onEvaluationChange,
  ]);

  const handleL1SMEFormChange = useCallback((data: SMEFormData, isValid: boolean) => {
        setFormData(data);
        onApprovalValidityChange(isValid);
        if (isValid && onEvaluationChange) {
          onEvaluationChange({
            awarded_marks: Number(data.marks),
            feedbacks: splitIntoSentences(data.remarks),
            attempt_status: data.attemptStatus as AttemptStatus,
          });
        }
  },[onApprovalValidityChange, onEvaluationChange, isOverrideMode])
  const handleSMEFormChange = useCallback(
    (data: SMEFormData, isValid: boolean) => {
      setFormData(data);
      // Only update validity and emit data when in override mode (editable)
      if (isOverrideMode) {
        onApprovalValidityChange(isValid);
        if (isValid && onEvaluationChange) {
          onEvaluationChange({
            awarded_marks: Number(data.marks),
            feedbacks: splitIntoSentences(data.remarks),
            attempt_status: data.attemptStatus as AttemptStatus,
          });
        } else if (!isValid && onEvaluationChange) {
          // Form is invalid, no save needed yet
          onEvaluationChange(null);
        }
      }
    },
    [onApprovalValidityChange, onEvaluationChange, isOverrideMode]
  );

  const handleStudentChallengeFormChange = useCallback(
    (data: RevisedEvaluationData, isValid: boolean) => {
      setFormData(data);
      onApprovalValidityChange(isValid);
      if (isValid && onEvaluationChange) {
        onEvaluationChange({
          awarded_marks: Number(data.marks),
          feedbacks: [data.remarks],
          attempt_status: data.attemptStatus,
        });
      } else if (!isValid && onEvaluationChange) {
        // Form is invalid, clear evaluation data
        onEvaluationChange(null);
      }
    },
    [onApprovalValidityChange, onEvaluationChange]
  );

  // Spring animation configuration
  const springConfig = {
    type: "spring" as const,
    stiffness: 300,
    damping: 25,
    mass: 0.8,
  };

  // Variant-specific animation directions
  const getVariantAnimation = (variant: EvaluationVariant) => {
    const baseVariants = {
      initial: { opacity: 0, scale: 0.9 },
      animate: {
        opacity: 1,
        scale: 1,
        transition: {
          ...springConfig,
          stiffness: 320,
          damping: 22,
        },
      },
      exit: {
        opacity: 0,
        scale: 0.9,
        transition: {
          ...springConfig,
          stiffness: 400,
          damping: 28,
        },
      },
    };

    // Different entry directions
    switch (variant) {
      case "L1Evaluated":
        return {
          ...baseVariants,
          initial: { ...baseVariants.initial, x: -30, rotateY: -5 },
          animate: {
            ...baseVariants.animate,
            x: 0,
            rotateY: 0,
          },
          exit: {
            ...baseVariants.exit,
            x: 30,
            rotateY: 5,
          },
        };
      case "SMEEvaluation":
        return {
          ...baseVariants,
          initial: { ...baseVariants.initial, y: 30, rotateX: -5 },
          animate: {
            ...baseVariants.animate,
            y: 0,
            rotateX: 0,
          },
          exit: {
            ...baseVariants.exit,
            y: -30,
            rotateX: 5,
          },
        };
      case "StudentChallenged":
        return {
          ...baseVariants,
          initial: { ...baseVariants.initial, x: 30, rotateY: 5 },
          animate: {
            ...baseVariants.animate,
            x: 0,
            rotateY: 0,
          },
          exit: {
            ...baseVariants.exit,
            x: -30,
            rotateY: -5,
          },
        };
      case "L1SMEEvaluation":
        return {
          ...baseVariants,
          initial: { ...baseVariants.initial, y: 30, rotateX: -5 },
          animate: {
            ...baseVariants.animate,
            y: 0,
            rotateX: 0,
          },
          exit: {
            ...baseVariants.exit,
            y: -30,
            rotateX: 5,
          },
        };
      default:
        return baseVariants;
    }
  };
  const renderContent = () => {
    const contentKey = `${currentVariant}-${question.question_id}`;

    switch (currentVariant) {
      case "L1Evaluated":
        if (!question.previous_evaluation_result) {
          return null;
        }
        return (
          <motion.div
            key={contentKey}
            variants={getVariantAnimation("L1Evaluated")}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <L1EvaluationCard
              result={question.previous_evaluation_result}
              maxMarks={question.max_marks}
              onAccept={handleAccept}
              onOverride={handleOverride}
              isAccepted={isAccepted}
            />
          </motion.div>
        );

      case "SMEEvaluation":
        if (
          !question.previous_evaluation_result &&
          !question.current_evaluation
        ) {
          return null;
        }
        return (
          <motion.div
            key={contentKey}
            variants={getVariantAnimation("SMEEvaluation")}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <SMEEvaluationForm
              previousResult={question.previous_evaluation_result!}
              currentEvaluation={question.current_evaluation}
              minMarks={question.min_marks}
              maxMarks={question.max_marks}
              isOverrideMode={isOverrideMode}
              onCancel={handleCancel}
              onOverride={handleOverride}
              onChange={handleSMEFormChange}
            />
          </motion.div>
        );

      case "StudentChallenged":
        if (!question.student_comment) {
          return null;
        }
        return (
          <motion.div
            key={contentKey}
            variants={getVariantAnimation("StudentChallenged")}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <StudentChallengeCard
              studentComment={question.student_comment}
              minMarks={question.min_marks}
              maxMarks={question.max_marks}
              onChange={handleStudentChallengeFormChange}
            />
          </motion.div>
        );
      case "L1SMEEvaluation":
        return (<motion.div
            key={contentKey}
            variants={getVariantAnimation("L1SMEEvaluation")}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <L1SMEEvaluationForm
              currentEvaluation={question.current_evaluation}
              minMarks={question.min_marks}
              maxMarks={question.max_marks}
              onChange={handleL1SMEFormChange}
            />
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div
      className={styles.container}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springConfig}
    >
      <motion.div
        className={styles.header}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ ...springConfig, delay: 0.1 }}
      >
        <h2 className={styles.title}>Evaluation & Approval</h2>
      </motion.div>
      <motion.div
        className={styles.divider}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ ...springConfig, delay: 0.15 }}
      />
      <div className={styles.contentSection}>
        <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
      </div>
    </motion.div>
  );
}
